import React, { PureComponent } from 'react';
import Select from 'react-select';
import { Button, Input, Tooltip, ResponsiveTable } from '..';

import LockedSVG from 'assets/icons/locked.svg';
import UnlockedSVG from 'assets/icons/unlocked.svg';

import styles from './styles';

const sizeOptions = [
  { value: '100', label: 'No Change' },
  { value: '75', label: '25% Smaller' },
  { value: '50', label: '50% Smaller' },
  { value: '25', label: '75% Smaller' },
  { value: 'custom', label: 'Custom Size' }
];
const resizeOptions = [
  { value: 'pixels', label: 'pixels' },
  { value: 'percent', label: 'percent' },
  { value: 'inch', label: 'inch' },
  { value: 'cm', label: 'cm' },
  { value: 'mm', label: 'mm' }
];
const ppiOptions = [
  { value: '72', label: '72' },
  { value: '150', label: '150 (Default)' },
  { value: '300', label: '300' }
];
const numberInputProps = {
  border: true,
  // suffix: resizeOptions,
  type: 'number',
  min: '1',
  step: '1',
  style: {
    margin: '10px 0'
  }
};

function getFloat(num) {
  const parsed = parseFloat(num);

  return isNaN(parsed) ? 0 : parseInt((parsed * 100), 10) / 100;
}
// function getInt(num) {
//   const parsed = parseInt(num, 10);

//   return isNaN(parsed) ? 0 : parsed;
// }

export default class ImageResizer extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      size: '100',
      imageWidth: props.originalImage ? props.originalImage.dimensions.width : 0,
      imageHeight: props.originalImage ? props.originalImage.dimensions.height : 0,
      imageRotation: props.cropper ? props.cropper.rotation : 0,
      aspectRatio: props.originalImage ? props.originalImage.dimensions.width / props.originalImage.dimensions.height : null,
      customWidth: 0,
      customHeight: 0,
      suffix: 'pixels',
      ppi: '150'
    };
  }

  onChangeSize = ({ value }) => this.setState({ size: value });

  // onChangeWidth = (width) => this.setState({
  //   customWidth: this.state.imageWidth ? Math.min(getFloat(width), this.state.imageWidth) : getFloat(width)
  // });
  onChangeWidth = (width) => this.setState(({customHeight, aspectRatio, suffix}) => ({
    customWidth: getFloat(width),
    customHeight: suffix === 'percent' ? getFloat(width) : aspectRatio ?  getFloat(width / aspectRatio) : customHeight
  }));

  onChangeHeight = (height) => this.setState(({customWidth, aspectRatio, suffix}) => ({
    customHeight: getFloat(height),
    customWidth: aspectRatio ? getFloat(height * aspectRatio) : customWidth
  }));

  onChangeSelect = (key) => ({ value }) => this.props.onOptionChange({ [key]: value });
  onChangeSuffix = ({ value }) => this.setState({suffix: value});
  onChangePPI = ({ value }) => this.setState({ppi: value});

  onImageRotated = (rotation) => this.setState({
    imageRotation: rotation
  });

  onCropModified = () => {
    const { width, height, aspectRatio } = this.props.cropper.getImageSize();

    this.setState({
      imageWidth: width,
      imageHeight: height,
      aspectRatio: aspectRatio ? width / height : null
    });
  };

  getResizeEstimate = () => {
    const {
      size,
      customWidth,
      customHeight,
      suffix,
      ppi,
      imageWidth,
      imageHeight
    } = this.state;

    // User has specified a width/height
    if (size === 'custom') {

      if (customWidth || customHeight) {
        let width, height;

        switch(suffix) {
          case 'percent':
            width = imageWidth * getFloat(customWidth) / 100;
            height = imageHeight * getFloat(customHeight) / 100;
            break;
          case 'inch':
            width  = customWidth  * ppi;
            height = customHeight * ppi;
            break;
          case 'cm':
            width  = (customWidth * ppi) / 2.54;
            height = (customHeight * ppi) / 2.54;
            break;
          case 'mm':
            width  = (customWidth * ppi) / 25.4;
            height = (customHeight * ppi) / 25.4;
            break;
          default: 
            width = customWidth;
            height = customHeight;
            break;
        }

        return {
          widthMultiplier: suffix === 'percent' ? getFloat(customWidth) / 100 : null,
          heightMultiplier: suffix === 'percent' ? getFloat(customHeight) / 100 : null,
          width: Math.round(width ? width : (height / imageHeight) * imageWidth),
          height: Math.round(height ? height : (width / imageWidth) * imageHeight)
        };
      }

      // No width/height specified, so we aren't resizing anything
      return {
        width: imageWidth,
        height: imageHeight
      };
    }

    const multiplier = getFloat(size) / 100;

    return {
      multiplier,
      width: Math.round(imageWidth * multiplier),
      height: Math.round(imageHeight * multiplier)
    };
  };

  componentDidMount() {
    const { cropper } = this.props;

    if (cropper) {
      cropper.on('cropApplied', this.onCropModified);
      cropper.on('cropReset', this.onCropModified);
      cropper.on('imageRotated', this.onImageRotated);
    }
  }

  componentWillUnmount() {
    const { cropper } = this.props;

    if (cropper) {
      cropper.off('cropApplied', this.onCropModified);
      cropper.off('cropReset', this.onCropModified);
      cropper.off('imageRotated', this.onImageRotated);
    }
  }

  componentDidUpdate() {
    const { onOptionChange } = this.props;
    const {
      multiplier,
      width,
      widthMultiplier,
      height,
      heightMultiplier
    } = this.getResizeEstimate();
    const { ppi, suffix } = this.state;
    let obj = {
      multiplier,
      width,
      widthMultiplier,
      height,
      heightMultiplier,
      suffix
    };
    if(suffix === 'inch' || suffix === 'cm' || suffix === 'mm') {
      obj['ppi'] = ppi;
    }
    onOptionChange(obj);
  }
  renderPPI() {
    const { suffix, ppi } = this.state;

    if (suffix !== 'pixels' && suffix !== 'percent') {
      return (
        <div className={styles.halfWidth}>
          Select DPI (optional)
          <Tooltip hint="DPI = Dots per inches. If you specify image size in mm, cm, or inches, we will store the DPI information in image metadata (JPG only). So that your printers can print at the specified size. If unsure, leave at default." />
          <Select
            name='ppi'
            value={ppi}
            options={ppiOptions}
            onChange={this.onChangePPI}
            clearable={false}
          />
        </div>
      )
    } else {
      return (<div className={styles.halfWidth}></div>);
    }
  }
  onRatioLockClick = () => {
    const {aspectRatio, customWidth, suffix, imageWidth, imageHeight} = this.state;
    if (aspectRatio) {
      this.setState({aspectRatio: null})
    } else {
      const ratio = imageWidth / imageHeight;
      
      this.setState({
        aspectRatio: ratio || null,
        customHeight: ratio ? suffix === 'percent' ? customWidth : getFloat(customWidth / ratio) : 0
      })
    }
  };

  renderAspectRatioLock() {
    const {aspectRatio} = this.state;
    const isLocked = !!aspectRatio;

    return (
      <Button title="Click to ON/OFF Aspect Ratio" alt="Click to ON/OFF Aspect Ratio" className={styles.lockButton} onClick={this.onRatioLockClick}>
        {isLocked ? <LockedSVG /> : <UnlockedSVG />}
      </Button>
    );
  }
  renderSizeOptions() {
    const { originalImage } = this.props;
    const { size, customWidth, customHeight, imageRotation, suffix } = this.state;
    const shouldRotateImage = imageRotation === 90 || imageRotation === 270;

    // If custom options are desired, we show some more fields
    if (size === 'custom') {
      let dimensions = '';
      if(suffix === 'percent') {
        dimensions = (
        <div className={styles.sideBySideInputsEverywhere}>
          <Input
            label='Custom Percentage'
            value={(shouldRotateImage ? customHeight : customWidth) || ''}
            onChange={shouldRotateImage ? this.onChangeHeight : this.onChangeWidth}
            {...numberInputProps}
          />
        </div>
        )
      } else {
        dimensions = (
        <div className={styles.sideBySideInputsEverywhere}>
          <Input
            className={!originalImage ? styles.halfWidth : styles.oneThirdWidthEverywhere}
            label='Width'
            value={(shouldRotateImage ? customHeight : customWidth) || ''}
            onChange={shouldRotateImage ? this.onChangeHeight : this.onChangeWidth}
            {...numberInputProps}
          />
          {!originalImage ? '' : this.renderAspectRatioLock()}
          <Input
            className={!originalImage ? styles.halfWidth : styles.oneThirdWidthEverywhere}
            label='Height'
            value={(shouldRotateImage ? customWidth : customHeight) || ''}
            onChange={shouldRotateImage ? this.onChangeWidth : this.onChangeHeight}
            {...numberInputProps}
          />
        </div>
        )
      }
      return (
        <div>
          <div className={styles.sideBySideInputs}>
            <div className={styles.twoThirdWidth}>
              {dimensions}
            </div>
            <div className={styles.oneThirdWidth}>
              <div className={styles.marginx}>
                Unit
                <Select
                  name='suffix'
                  value={suffix}
                  options={resizeOptions}
                  onChange={this.onChangeSuffix}
                  clearable={false}
                  />
              </div>
            </div>
          </div>
          {this.renderPPI()}
        </div>
      );
    }

    return null;
  }

  renderSizeInfo() {
    const { originalImage } = this.props;

    if (!originalImage) {
      return null;
    }

    const { imageWidth, imageHeight, imageRotation } = this.state;
    const resizeEstimate = this.getResizeEstimate();
    const shouldRotateImage = imageRotation === 90 || imageRotation === 270;
    const columns = {
      rowHeading: {
        heading: '',
        width: '150px'
      },
      width: {
        grow: true,
        heading: 'Width',
        width: '150px'
      },
      height: {
        grow: true,
        heading: 'Height',
        width: '150px'
      }
    };
    const rows = [{
      rowHeading: 'Original (in px)',
      width: originalImage.dimensions.width,
      height: originalImage.dimensions.height
    }, {
      rowHeading: 'Cropped',
      width: shouldRotateImage ? imageHeight : imageWidth,
      height: shouldRotateImage ? imageWidth : imageHeight
    }, {
      rowHeading: 'Estimate Final',
      width: shouldRotateImage ? resizeEstimate.height : resizeEstimate.width,
      height: shouldRotateImage ? resizeEstimate.width : resizeEstimate.height
    }];

    return (
      <div className='section'>
        Image Size Info
        <ResponsiveTable
          columns={columns}
          rows={rows}
          type="default-static"
        />
      </div>
    );
  }

  render() {
    const { originalImage } = this.props;
    const { size } = this.state;

    return (
      <div className={originalImage ? 'twoSections' : ''}>
        <div className='section'>
          Image Size
          <Select
            className={!originalImage ? styles.halfWidth : ''}
            name='imageSize'
            value={size}
            options={sizeOptions}
            onChange={this.onChangeSize}
            clearable={false}
          />
          {this.renderSizeOptions()}
        </div>
        {this.renderSizeInfo()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ImageResizer/index.jsx